<script lang="ts" setup>
import { CharacteristicType, InstallationType, KeyNumType, StringMap, ValidationRuleType } from '@/helper/Types'
import userStore from '@/store/UserStore'
import projectStore from '@/store/ProjectStore'
import { calculateDisplayPrice, copyToClipboard, formatPrice } from '@/helper/tools'
import ProjectController from '@/controllers/ProjectController'
import SummaryProduct from '@/components/steps/summary/SummaryProduct.vue'
import { ProductResponse } from '@/services/models/ProductResponse'
import { HPCharacteristic } from '@/services/models/HPCharacteristic'
import i18n, { formatNumber } from '@/helper/Translations'
import { Characteristic } from '@/services/models/Characteristic'
import { DHWStorageCharacteristic } from '@/services/models/DHWStorageCharacteristic'
import { BackupHeaterCharacteristic } from '@/services/models/BackupHeaterCharacteristic'
import { camelize, ValidatorType } from '@/helper/ValidateDirective'
import QuantityBadge from '@/components/badges/QuantityBadge.vue'
import appStore from '@/store/AppStore'
import PriceInput from '@/components/PriceInput.vue'
import PriceCalculator from '@/components/steps/summary/PriceCalculator.vue'
import formStore from '@/store/FormStore'
import { AccessoryCharacteristic } from '@/services/models/AccessoryCharacteristic'
import { SalesPackageCharacteristic } from '@/services/models/SalesPackageCharacteristic'
import { Heatpump } from '@/services/models/implementations/Heatpump.ts'
import { Product } from '@/services/models/implementations/Product.ts'
import { Article } from '@/services/models/Article.ts'
import SalesPackageAccessory from '@/components/steps/summary/SalesPackageAccessory.vue'

const props = defineProps<{
  salesPackage: Article,
  enableCustomPrices: boolean,
  validator: ValidatorType
}>()

const emit = defineEmits<{
  (e: 'blurCustomPrice'): void
}>()

const salesPackageArticles =
  props.salesPackage.details?.getProductCharacteristic<SalesPackageCharacteristic>(CharacteristicType.SALES_PACKAGE).articles
    .map(a => new Article(a)) || []

const salesPackageTotalPrice = (props.salesPackage.details?.price || 0) * props.salesPackage.quantity

const salesPackageComponents = salesPackageArticles
  .filter(article => !article.details?.getProductCharacteristic(CharacteristicType.HEAT_PUMP))
  .filter(article => !article.details?.getProductCharacteristic(CharacteristicType.APPLIANCE_INTERFACE))
  .filter(article => !article.details?.getProductCharacteristic(CharacteristicType.ACCESSORY))

const salesPackageAccessories = salesPackageArticles
  .filter(article => article.details?.getProductCharacteristic<AccessoryCharacteristic>(CharacteristicType.ACCESSORY)
    || article.details?.getProductCharacteristic<AccessoryCharacteristic>(CharacteristicType.APPLIANCE_INTERFACE))

const selectedPackageHeatPump = salesPackageArticles
  .find(article => article.details?.getProductCharacteristic<HPCharacteristic>(CharacteristicType.HEAT_PUMP))

function getHpProductSpecs(product: Product): StringMap {
  const hp = product.getProductCharacteristic<HPCharacteristic>(CharacteristicType.HEAT_PUMP)

  const integratedDhwStorage: DHWStorageCharacteristic = product.getProductCharacteristic(CharacteristicType.DHW_STORAGE)
  const specs: StringMap = {}

  if (integratedDhwStorage) {
    specs['storage'] = `${i18n.global.t('step_heat_pumps.table_storage')}: ${integratedDhwStorage.volume} l`
  }

  const heating = projectStore.installationTypeName(InstallationType.HEATING)
  if (projectStore.isHeating) {
    specs['heatingPerformance'] =
      `${i18n.global.t(`components.heating_performance.${heating}`)}: ${formatNumber(projectStore.getHeatingPowerOutput(hp))} kW`

    specs['heatingCoverage'] =
      `${i18n.global.t(`components.heat_loss_coverage.${heating}`)}: ${hp.heating?.loadCoveragePercent?.toFixed(0)} %`

    specs['bivalencePoint'] =
      `${i18n.global.t('components.bivalence_point')}: ${Math.round(hp.heating?.operatingPointAtBivalencePoint?.sourceTemperature || 0)} °C`
  }

  const cooling = projectStore.installationTypeName(InstallationType.COOLING)
  if (projectStore.isCooling) {
    specs['coolingPerformance'] =
      `${i18n.global.t(`components.heating_performance.${cooling}`)}: ${formatNumber(hp.cooling?.operatingPoint.powerOutput)} kW`

    specs['coolingCoverage'] =
      `${i18n.global.t(`components.heat_loss_coverage.${cooling}`)}: ${hp.cooling?.loadCoveragePercent?.toFixed(0)} %`
  }

  return specs
}

function getComponentSpecs(prod: ProductResponse): StringMap {
  return prod.characteristics.reduce((acc: StringMap, chr: Characteristic) => {
    const res: StringMap = acc || {}

    switch (chr.type) {
      case CharacteristicType.BUFFER_TANK:
      case CharacteristicType.COOLING_BUFFER_TANK:
      case CharacteristicType.DHW_STORAGE:
        res['volume'] =
          `${i18n.global.t('step_components.table_storage_volume')}: ${formatNumber((chr as DHWStorageCharacteristic).volume)} l`
        break
      case CharacteristicType.BACKUP_HEATER:
        res['power'] =
          `${i18n.global.t('step_components.table_power')}: ${formatNumber((chr as BackupHeaterCharacteristic).powerOutput)} kW`
        res['fuel'] =
          `${i18n.global.t('step_components.table_fuel_type')}: ${i18n.global.t(`fuel_types.${(chr as BackupHeaterCharacteristic).fuelType}`)}`
        break
    }

    return res
  }, {} as StringMap)
}

function setCustomprice(salesPackage: Article, newPrice: string | number | undefined) {
  (formStore.data.customPrices as KeyNumType)[salesPackage.id] = newPrice as number
  emit('blurCustomPrice')
}

defineExpose({ validator: props.validator })
</script>

<template>
  <div :data-id="salesPackage.id" class="is--package">
    <div class="package-content">
      <QuantityBadge v-if="salesPackage.quantity > 1" :quantity="salesPackage.quantity" />

      <div
        :class="{'margin-left': salesPackage.quantity > 1}" class="package-headline"
        @click.alt.prevent="copyToClipboard(salesPackage.id)"
      >
        <h3>{{ salesPackage.details?.name }}</h3>
        <p>
          <b>{{ $t('step_summary.package_order_number') }}: </b>
          <span class="order-number">{{ salesPackage.details?.orderNumber }}</span>
        </p>
      </div>

      <SummaryProduct
        v-if="selectedPackageHeatPump?.details"
        :is-package-product="true"
        :product="selectedPackageHeatPump.details"
        :product-specs="getHpProductSpecs(selectedPackageHeatPump.details as Heatpump)"
        :product-title="$t('step_summary.title_heating_system')"
        :quantity="selectedPackageHeatPump.quantity"
        :validator="validator"
        product-type="heat-pump"
      />

      <SummaryProduct
        v-for="article in salesPackageComponents"
        :key="`package-component-${article.id}`"
        :is-package-product="true"
        :product="article.details"
        :product-specs="getComponentSpecs((article as Article).details)"
        :product-title="ProjectController.getProductTitle(article?.details)"
        :quantity="article.quantity"
        :validator="validator"
        product-type="component"
      />

      <h3 v-if="salesPackageAccessories.length">
        <b>{{ $t('steps.accessories') }}</b>
      </h3>

      <div v-if="salesPackageAccessories.length" class="package__accessories">
        <SalesPackageAccessory
          v-for="accessory in salesPackageAccessories"
          :key="accessory.id"
          :accessory="accessory"
        />
      </div>
    </div>

    <div v-if="!userStore.hidePrices" :class="{ 'is--custom-price': enableCustomPrices }" class="package__badge badge">
      <div class="badge__content">
        <div>
          <b>
            <span>{{ salesPackage.quantity > 1 ? $t('step_summary.package_total_price') : $t('step_summary.package_price') }}:</span>
          </b>
        </div>

        <div v-if="enableCustomPrices">
          <div v-if="enableCustomPrices" class="custom-price">
            <div class="custom-price__input-container">
              <PriceInput
                :id="`custom-price-${salesPackage.id}`"
                v-model:price="(formStore.data.customPrices as KeyNumType)[salesPackage.id]"
                v-validate="ValidationRuleType.POSITIVE_ZERO_DECIMAL"
                :disabled="appStore.isLoading"
                :error="validator[camelize(`custom-price-${salesPackage.id}`)]?.error.value"
                :original-price="salesPackageTotalPrice"
                @blur="$emit('blurCustomPrice')"
              />

              <PriceCalculator
                :id="salesPackage.id"
                :original-price="salesPackageTotalPrice"
                @confirm="customPrice => setCustomprice(salesPackage, customPrice)"
              />
            </div>

            <div class="custom-price__infos">
              <div>{{ $t('step_summary.list_price') }}: {{ formatPrice(salesPackageTotalPrice) }}</div>
              <div>&nbsp;{{ $t('step_summary.table_vat_excluded') }}</div>
            </div>
          </div>
        </div>

        <div v-else>
          <div class="badge__caption package-price">
            {{ calculateDisplayPrice(salesPackageTotalPrice) }}
          </div>

          <div class="package-vat">
            <span v-if="userStore.vatRate && userStore.enableDisplayOfVatPrices">
              {{ $t('step_summary.table_vat_included', { vat: userStore.vatRate }) }}
            </span>

            <span v-else>{{ $t('step_summary.table_vat_excluded') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

$maxBadgeWidth: 230;
$maxBadgeWidthCustomPrice: 270;
$summaryPadding: 32;
$summaryPaddingMobile: 32;

.is--package {
  display: flex;
  align-items: flex-start;
  margin-bottom: 4rem;

  .package-content {
    flex: 1;
  }

  .package-headline {
    margin-bottom: 1rem;

    h3 {
      margin-bottom: rem-calc(12);
      display: block;

      @include themify($themes) {
        font-size: rem-calc(themed('h2Size'));
        font-weight: bold;
        line-height: rem-calc(themed('h2LineHeight'));
        color: themed('h2Color');
        margin-bottom: rem-calc(themed('h2Margin'));

        @include media('<desktop') {
          font-size: rem-calc(20);
          margin-bottom: rem-calc(4);
        }
      }
    }

    &.margin-left {
      margin-left: 4rem;
    }
  }

  .package__badge {
    right: rem-calc(-41);
    max-width: rem-calc($maxBadgeWidth);
    z-index: 1;

    &.badge {
      position: relative;
    }

    &.is--custom-price {
      max-width: rem-calc($maxBadgeWidthCustomPrice);
    }

    .price-input {
      color: $textColor;
      font-weight: bold;
      margin-right: rem-calc(8);

      @include themify($themes) {
        background-color: themed('primaryColor');
      }
    }

    .custom-price__infos {
      color: #fff;
      flex-wrap: wrap;
    }

    .custom-price {
      margin-top: rem-calc(8);
    }

    & + .item {
      padding-right: rem-calc($maxBadgeWidth - $summaryPaddingMobile);

      @include media('>desktop') {
        padding-right: rem-calc($maxBadgeWidth - $summaryPadding);
      }
    }

    &.is--custom-price + .item {
      padding-right: rem-calc($maxBadgeWidthCustomPrice - $summaryPaddingMobile);

      @include media('>desktop') {
        padding-right: rem-calc($maxBadgeWidthCustomPrice - $summaryPadding);
      }
    }
  }

  :deep(.item__title) {
    font-weight: normal;
    font-size: inherit;
  }

  :deep(.item__name) {
    @include themify($themes) {
      color: $textColor;
    }
  }
}
</style>
